/* eslint-disable radix */
import React, { useEffect, useMemo, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { parse, ParseAsyncConfig, Parser, ParseStepResult } from 'papaparse';
import styled from 'styled-components';
import validator from 'validator';
import {
  Button,
  FileUploadButton,
  FileUploadDropZone,
  H3,
  HeaderCell,
  HeaderRow,
  LayoutSidebar,
  LoaderModal,
  Message,
  ModalFactory,
  SidebarButton,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarHeaderTitle,
  SidebarList,
  SidebarListItem,
  SidebarListItemLabel,
  SidebarListItemValue,
  SidebarTitle,
  Table,
  TableBody,
  TableHead,
  TooltipIcon,
  TooltipWrapper,
} from 'components';
import { theme, toRem } from 'design';
import { Appearance } from 'design/types';
import { UploadPaymentMatch } from 'modals';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { Payment, uploadPaymentRequest } from 'store/Payment';
import { ICurrentEmployee, IStore } from 'store/types';
import { IEmployee, ISiteRolePolicy, IUploadPayment } from 'types';
import { centsToDollarsDisplay, isEmployeePayable, sumBy } from 'utils';
import { AnimatedSidebarSection, MPContent, SidebarContentLayout, SidebarHeaderSection, SidebarMobileActions, SidebarSection } from './components';
import UploadPaymentRequirements from './components/UploadPaymentRequirements/UploadPaymentRequirements';
import UploadPaymentSelector from './components/UploadPaymentSelector/UploadPaymentSelector';
import MakePaymentHeader from './MakePaymentHeader';
import { MakePaymentSidebarProps } from './MakePaymentTypes';

interface IUploadPaymentProps extends MakePaymentSidebarProps {
  pageHeaderContent: React.ReactNode;
  currentEmployee: ICurrentEmployee;
  currentSiteId: string;
  onAddUploadPaymentToPayeesList: (employee: IEmployee, tipAmount: number) => void;
  onRemoveAllPayees: () => void;
  paymentUploads: IUploadPayment[];
  setPaymentUploads: (payments: IUploadPayment[]) => void;
  isEmployeeDisabled: (emp: IEmployee) => string;
  sidebarOpen: boolean;
  tipPoolEnabled: boolean;
  tipPoolLimit?: number;
  rolePolicy?: ISiteRolePolicy;
}

const UploadContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding-bottom: ${toRem(16)};
`;

const ImportContainer = styled.div`
  width: 100%;
  flex-flow: column nowrap;
`;

const StyledMessage = styled(Message)`
  margin-bottom: ${toRem(16)};
`;

const PAYMENT_LIMIT = 100;

const UploadPayment = ({
  pageHeaderContent,
  currentEmployee,
  currentSiteId,
  onBack,
  onNext,
  onRemoveAllPayees,
  onAddUploadPaymentToPayeesList,
  paymentUploads,
  setPaymentUploads,
  isEmployeeDisabled,
  sidebarOpen,
  onExpand,
  tipPoolEnabled,
  tipPoolLimit,
  rolePolicy,
}: IUploadPaymentProps) => {
  const dispatch = useDispatch();
  const [uploadError, setUploadError] = useState<string | undefined>(undefined);
  const [isEmployeeIdUpload, setIsEmployeeIdUpload] = useState<boolean>(false);

  const [isBuildingPayeeList, setIsBuildingPayeeList] = useState<boolean>(false);
  const [selectedPayment, setSelectedPayment] = useState<{ index: number; payment: IUploadPayment } | undefined>(undefined);
  const isImportingData = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Payment.UPLOAD_PAYMENT_REQUEST));

  useEffect(() => {
    if (paymentUploads.length > 0 && isBuildingPayeeList === true) {
      paymentUploads.forEach((payment) => {
        if (payment.employee) onAddUploadPaymentToPayeesList(payment.employee, payment.amount);
      });

      setIsBuildingPayeeList(false);
      onNext();
    } else {
      setIsBuildingPayeeList(false);
    }
  }, [paymentUploads, onNext, onAddUploadPaymentToPayeesList, isBuildingPayeeList]);

  const parsedPayments: IUploadPayment[] = [];

  const handleCsvUpload = (file: any) => {
    let rowNum = 1; // Start indexing at 1 to accommodate header row

    const parseConfig: ParseAsyncConfig = {
      header: true,
      skipEmptyLines: 'greedy',
      dynamicTyping: true, // convert number and boolean values from strings to their type
      transformHeader: (header: string) => header.trim().toLowerCase().split(' ').join(''), // trim whitespace and concatenate headers
      step: (results: ParseStepResult<any>, parser: Parser) => {
        // Abort upload if payment limit reached
        if (rowNum > PAYMENT_LIMIT) {
          setUploadError(`File cannot contain more than ${PAYMENT_LIMIT} payments.`);
          parser.abort();
          return;
        }

        const hasAmountHeader = results.meta.fields?.includes('amount');
        const hasNameHeader = results.meta.fields?.includes('name');
        const hasEmployeeIdHeader = results.meta.fields?.includes('employeeid');
        const includesModifiedNameHeader = results.meta.fields?.includes('firstname') && results.meta.fields?.includes('lastname');

        rowNum++;

        // Required CSV Headers: `name` and `amount` or 'firstname', 'lastname' and 'amount'
        if (!hasAmountHeader || (!hasNameHeader && !includesModifiedNameHeader && !hasEmployeeIdHeader)) {
          setUploadError('There was an error uploading your file. The header values are invalid. Please review the formatting and try again.');
          parser.abort();
          return;
        }

        // Format Name
        if ((hasNameHeader || includesModifiedNameHeader) && (!results.data.firstname || !results.data.lastname) && !results.data.name) {
          setUploadError(`There was an error uploading your file. Please review the formatting and try again. (Error on Line ${rowNum})`);
          parser.abort();
          return;
        }

        // Validate Amount
        if (!validateAmount(results.data.amount)) {
          setUploadError(`There was an error uploading your file. An invalid amount value was found. Please review the formatting and try again. (Error on Line ${rowNum})`);
          parser.abort();
          return;
        }

        // Validate Name
        if ((hasNameHeader && typeof results.data.name !== 'string') || (includesModifiedNameHeader && (typeof results.data.firstname !== 'string' || typeof results.data.lastname !== 'string'))) {
          setUploadError(`There was an error uploading your file. An invalid name value was found. Please review the formatting and try again. (Error on Line ${rowNum})`);
          parser.abort();
          return;
        }

        // Set state to show employee ID header
        setIsEmployeeIdUpload(!!hasEmployeeIdHeader);

        // Load parsed payment
        parsedPayments.push({
          amount: formatAmount(results.data.amount),
          ...(hasEmployeeIdHeader && { employeeId: results.data.employeeid.toString().trim() }),
          ...(includesModifiedNameHeader && { name: `${results.data.firstname?.trim()} ${results.data.lastname?.trim()}` }),
          ...(hasNameHeader && { name: results.data.name.trim() }),
        });
      },
      complete: (results: ParseStepResult<any>) => {
        // Handle empty file
        if (!results.meta.aborted && parsedPayments.length === 0) {
          setUploadError('File is empty');
          return;
        }

        // Process upload payments (Match Employees by name)
        if (!results.meta.aborted && parsedPayments.length > 0) {
          dispatch(uploadPaymentRequest(currentSiteId, parsedPayments, (payments) => setPaymentUploads(payments)));
        }
      },
      error: (err: Error) => {
        setUploadError(err.message);
      },
    };

    if (!file) return;

    // clear the uploaded payment state
    clearUpload();

    parse(file, parseConfig);
  };

  const validateAmount = (amount: any): boolean => {
    if (!amount) return true;
    return validator.isCurrency(amount.toString().trim(), { allow_negatives: false, digits_after_decimal: [1, 2] });
  };

  const formatAmount = (tipAmount: any) => {
    if (!tipAmount) return 0;

    const tipAmountAsString = tipAmount.toString().replaceAll('$', '');
    // if there are no decimals, return the amount in cents
    // Ex. 100 becomes 10000 which will evaluate as $100.00 in the payment form
    if (tipAmountAsString.split('.').length === 1) return parseInt(tipAmountAsString, 0) * 100;

    // Decimals ending in 0 are ignored during the file upload (100.50 imports as 100.5) so we need to check if there
    // is only one decimal place and append the 0 back on so the value correctly evaluates as cents
    // Ex. 100.5 becomes 10050 which will evaluate as $100.50 in the payment form
    if (tipAmountAsString.split('.')[1].length === 1) return parseInt(tipAmountAsString.split('.').join(''), 0) * 10;

    // if there are decimal places, remove the decimal and return the whole number
    // Ex. 100.98 becomes 10090 which will evaluate as $100.98 in the payment form
    return parseInt(tipAmountAsString.split('.').join(''), 0);
  };

  const clearUpload = () => {
    setUploadError(undefined);
    setPaymentUploads([]);
    onRemoveAllPayees();
  };

  const removePaymentUpload = (index: number) => {
    if (paymentUploads.length > 0) {
      onRemoveAllPayees();
      setPaymentUploads(paymentUploads.filter((_, i) => i !== index));
    }
  };

  const setPaymentEmployee = (index: number, payment: IUploadPayment) => {
    setSelectedPayment({ index, payment });
    showPaymentModal();
  };

  const onUpdatePayment = (index: number, newPayment: IUploadPayment) => {
    if (paymentUploads.length > 0) {
      onRemoveAllPayees();
      setPaymentUploads(paymentUploads.map((p, i) => (i === index ? newPayment : p)));
      hidePaymentModal();
    }
  };

  const getEmployeeErrors = useMemo(() => {
    let errorTitle: string | undefined;
    let errorMessage: string | undefined;

    if (rolePolicy && paymentUploads?.some((upload) => upload.employee && !isEmployeePayable(upload.employee, rolePolicy, currentEmployee))) {
      errorTitle = 'Ineligible Match';
      errorMessage = 'You are not allowed to issue tips to one or more matched users. You can select a payable match or remove them in order to process this payment.';
    } else if (paymentUploads?.some((upload) => !upload.employee && !upload.employeeMatch.length)) {
      errorTitle = 'Match Not Found';
      errorMessage = 'One or more users were not matched with a Kickfin account. You can select a match or invite the employee to Kickfin in order to process this payment.';
    } else if (paymentUploads?.some((upload) => !upload.employee && upload.employeeMatch.length > 1)) {
      errorTitle = 'Multiple Matches Found';
      errorMessage = 'Multiple Kickfin account matches have been found for one or more users. You can select a match or invite the correct employee to Kickfin in order to process this payment.';
    }

    if (!errorMessage) return null;

    return <Message appearance={Appearance.DANGER} title={errorTitle} message={errorMessage} />;
  }, [paymentUploads]);

  const [showPaymentModal, hidePaymentModal] = useModal(
    () => (
      <ModalFactory customModal onClose={hidePaymentModal} isActive closeOnOutSideClick={false}>
        {selectedPayment && selectedPayment.payment && (
          <UploadPaymentMatch
            payment={selectedPayment.payment}
            onClose={hidePaymentModal}
            siteId={currentSiteId}
            onUpdatePayment={(payment: IUploadPayment) => onUpdatePayment(selectedPayment.index, payment)}
            isEmployeeDisabled={isEmployeeDisabled}
          />
        )}
      </ModalFactory>
    ),
    [selectedPayment]
  );

  const paymentsAreEmpty = isEmpty(paymentUploads);
  const disableNext = paymentsAreEmpty || !rolePolicy || paymentUploads.some((pu) => pu.employee === undefined || !isEmployeePayable(pu.employee, rolePolicy, currentEmployee));

  // TODO: Clean up the below variables that were copied from `PaymentInput.tsx` for the sidebar content
  // going to hard code this until we get confirmation that this should not be pulled from API
  const totalTips = paymentUploads ? sumBy(paymentUploads, 'amount') - 0 : 0;
  const siteUsesTipPool = true;

  // Payout Limit
  const calcTipPoolLimit = tipPoolLimit && tipPoolLimit > 0 ? tipPoolLimit : 0.0;
  // Remaining Payout
  const remainingTipPool: number | undefined = tipPoolLimit ? tipPoolLimit - totalTips : 0;
  const calcRemainingTipPool = remainingTipPool && remainingTipPool <= 0 ? 0 : remainingTipPool;
  const calcTipPoolOver: number | undefined = (() => {
    if (remainingTipPool === undefined || tipPoolLimit === undefined) return undefined;

    return remainingTipPool < 0 ? tipPoolLimit - totalTips : 0;
  })();

  const overTipPoolLimit = Boolean(remainingTipPool && remainingTipPool < 0);

  return (
    <LayoutSidebar
      content={
        <>
          <MakePaymentHeader data-cy="make-payment-header" pageHeaderContent={pageHeaderContent} />
          <MPContent>
            <LoaderModal isActive={paymentsAreEmpty && isImportingData} title="Loading Data" message="One moment please..." />
            {uploadError && <StyledMessage appearance={Appearance.DANGER} message={uploadError} />}
            {!isImportingData && paymentsAreEmpty && <FileUploadDropZone onUpload={handleCsvUpload} accept=".csv" />}
            {!uploadError && !paymentsAreEmpty && (
              <ImportContainer>
                <>
                  <UploadContainer>
                    <H3>Payment Upload</H3>
                    <FileUploadButton label="Upload New File" size="small" appearance="primary" accept=".csv" onUpload={handleCsvUpload} />
                  </UploadContainer>
                  <Table scrollable data-cy="upload-payment-table">
                    <TableHead>
                      <HeaderRow sticky>
                        <HeaderCell>{isEmployeeIdUpload ? 'Employee' : 'Name'}</HeaderCell>
                        <HeaderCell>Amount</HeaderCell>
                        <HeaderCell />
                      </HeaderRow>
                    </TableHead>
                    <TableBody>
                      {paymentUploads.map((payment: IUploadPayment, idx) => (
                        <UploadPaymentSelector
                          key={`upload-payment-${idx}`}
                          payment={payment}
                          onRemove={() => removePaymentUpload(idx)}
                          onSetEmployee={(pmt) => setPaymentEmployee(idx, pmt)}
                          isEmployeeIdUpload={isEmployeeIdUpload}
                          disabled={payment.employee && rolePolicy && !isEmployeePayable(payment.employee, rolePolicy, currentEmployee)}
                          tooltipId={`payee-upload-${idx}`}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </>
              </ImportContainer>
            )}
          </MPContent>
        </>
      }
      sidebar={
        paymentUploads.length === 0 || uploadError ? (
          <>
            <SidebarHeader>
              <SidebarHeaderTitle>
                <SidebarButton onClick={onExpand} isOpen={sidebarOpen} />
                <H3>Required Formats</H3>
              </SidebarHeaderTitle>

              <SidebarMobileActions columns={2}>
                <Button label="Back" appearance="white" size="small" disabled={false} onClick={onBack} />
              </SidebarMobileActions>
            </SidebarHeader>

            <SidebarContent>
              <SidebarContentLayout>
                <UploadPaymentRequirements />
              </SidebarContentLayout>
            </SidebarContent>

            <SidebarFooter>
              <Button label="Back" appearance="white" size="large" disabled={false} onClick={onBack} />
            </SidebarFooter>
          </>
        ) : (
          <>
            <SidebarHeader>
              <SidebarHeaderSection>
                <SidebarHeaderTitle>
                  <SidebarButton onClick={onExpand} isOpen={sidebarOpen} />
                  <SidebarTitle title="Payment Summary" />
                </SidebarHeaderTitle>

                <SidebarMobileActions columns={2}>
                  <Button label="Back" appearance="white" size="small" disabled={false} onClick={onBack} />
                  <Button label="Next" data-cy="mobile-next-btn" appearance="primary" size="small" disabled={disableNext} onClick={() => setIsBuildingPayeeList(true)} isProcessing={isBuildingPayeeList} />
                </SidebarMobileActions>
              </SidebarHeaderSection>
            </SidebarHeader>

            <SidebarContent>
              <SidebarContentLayout>
                {siteUsesTipPool && (
                  <AnimatedSidebarSection
                    initial={tipPoolEnabled}
                    animate={{
                      height: tipPoolEnabled ? 'auto' : 0,
                    }}
                    transition={theme.transition.spring.accordion}
                    isVisible={tipPoolEnabled}
                  >
                    <SidebarList>
                      <SidebarListItem>
                        <SidebarListItemLabel size="large">
                          <TooltipWrapper>
                            Payout Limit
                            <TooltipIcon id="limit-description">This is the pool of funds you can use to pay employees</TooltipIcon>
                          </TooltipWrapper>
                        </SidebarListItemLabel>
                        <SidebarListItemValue data-cy="tip-pool-limit-value">{centsToDollarsDisplay(calcTipPoolLimit)}</SidebarListItemValue>
                      </SidebarListItem>

                      <SidebarListItem>
                        <SidebarListItemLabel size="small">Remaining</SidebarListItemLabel>
                        <SidebarListItemValue data-cy="remaining-tip-pool">{centsToDollarsDisplay(calcRemainingTipPool)}</SidebarListItemValue>
                      </SidebarListItem>

                      <SidebarListItem>
                        <SidebarListItemLabel size="small">Over</SidebarListItemLabel>
                        <SidebarListItemValue color={overTipPoolLimit ? 'red' : 'default'} data-cy="over-tip-pool">
                          {calcTipPoolOver ? calcTipPoolOver < 0 && '(' : null}
                          {centsToDollarsDisplay(Math.abs(calcTipPoolOver || 0))}
                          {calcTipPoolOver ? calcTipPoolOver < 0 && ')' : null}
                        </SidebarListItemValue>
                      </SidebarListItem>
                    </SidebarList>
                  </AnimatedSidebarSection>
                )}

                <SidebarSection>
                  <SidebarList>
                    <SidebarListItem>
                      <SidebarListItemLabel size="large">Total</SidebarListItemLabel>
                      <SidebarListItemValue size="large" data-cy="total-paid">
                        {totalTips > 0 ? centsToDollarsDisplay(totalTips) : '$0.00'}
                      </SidebarListItemValue>
                    </SidebarListItem>
                  </SidebarList>
                </SidebarSection>
                {getEmployeeErrors}
              </SidebarContentLayout>
            </SidebarContent>

            <SidebarFooter>
              <Button label="Back" appearance="white" size="large" disabled={false} onClick={onBack} />
              <Button label="Next" data-cy="next-btn" appearance="primary" size="large" disabled={disableNext} onClick={() => setIsBuildingPayeeList(true)} isProcessing={isBuildingPayeeList} />
            </SidebarFooter>
          </>
        )
      }
      sidebarOpen={sidebarOpen}
    />
  );
};

export default UploadPayment;
